import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiGet, apiPost } from "../lib/api";
import type {
  BulkApproveResponse,
  ReviewStatus,
  ReviewSummary,
  ReviewsListResponse,
} from "../lib/types";
import { StarRating } from "../components/StarRating";
import { StatusBadge } from "../components/StatusBadge";
import { timeAgo } from "../lib/format";
import { ReviewDetail } from "./ReviewDetail";
import { useScope } from "../scope/ScopeContext";
import {
  customRangeLabel,
  dateWindowOptions,
  lastFullMonth,
  rangeProblem,
  todayInEastern,
  type CustomRange,
  type WindowBasis,
} from "../lib/date-windows";
import { CustomRangeFields } from "../components/CustomRangeFields";
import { nextSelection, type ReloadReason } from "../lib/queue_selection";

/**
 * A review that must not slide through on autopilot: 1–2 stars, negative
 * sentiment, or a draft the model itself flagged for manager escalation.
 * These sort to the top, get loud styling, and are excluded from Approve all.
 */
function isSnoozed(r: ReviewSummary): boolean {
  return Boolean(r.snoozedUntil && new Date(r.snoozedUntil) > new Date());
}

function needsAttention(r: ReviewSummary): boolean {
  if (r.status === "posted" || r.status === "skipped") return false;
  // Snoozing/hiding is the staff saying "not now" — stop shouting about it.
  if (r.hiddenAt || isSnoozed(r)) return false;
  return (
    r.stars <= 2 ||
    r.sentiment === "negative" ||
    Boolean(r.latestDraft?.needsEscalation)
  );
}

const STATUS_FILTERS: { value: ReviewStatus; label: string }[] = [
  { value: "new", label: "New" },
  { value: "drafted", label: "Drafted" },
  { value: "ready", label: "Ready" },
  { value: "posted", label: "Posted" },
  { value: "skipped", label: "Skipped" },
];

/**
 * Age window on the queue. Don Elwell (Alan Jay), 2026-08-06: "can the old
 * reviews be removed from the list to review? Draw the line at the last 2 weeks
 * or so." 14 days is the default; the older set is never hidden, it moves behind
 * the "Older" chip with its count on the label.
 */
const windowOptions = (
  olderOnly: boolean,
  basis: WindowBasis,
): { days: number | null; label: string }[] => [
  ...dateWindowOptions(olderOnly, basis),
  { days: null, label: basis === "reply" ? "All replies" : "All time" },
];
const DEFAULT_WINDOW_DAYS = 14;

/**
 * Who posted the reply: through this tool, or typed straight into Google.
 * Same rule as the server's `replySourceOf`, which the "Via Savvy only" chip
 * filters on — keep the two in step so the chip and the label never disagree.
 */
function replySource(r: ReviewSummary): string | null {
  if (r.status !== "posted") return null;
  const d = r.latestDraft;
  return d?.postedAt && d.modelId !== "dba-import" ? "via Savvy" : "direct on Google";
}

/** Rows per page. The rest arrives via "Load more", never silently omitted. */
const PAGE_SIZE = 100;

export function ReviewsQueuePage() {
  const { focusedDealerId, dealersInFocus } = useScope();
  const [reviews, setReviews] = useState<ReviewSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Adam's call 2026-08-06: a review that's already been responded to must NOT
  // be in the list — the default view is the actionable queue only. Posted
  // (replied by us OR answered directly on Google) is behind its filter chip.
  const [activeStatuses, setActiveStatuses] = useState<Set<ReviewStatus>>(
    () => new Set<ReviewStatus>(["new", "drafted", "ready"]),
  );
  const [activeStoreIds, setActiveStoreIds] = useState<Set<string>>(new Set());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Below lg the list and the reply pane share one column: tapping a row opens
  // the pane, "Back" returns to the list. Auto-selection never opens it.
  const [detailOpen, setDetailOpen] = useState(false);
  const [showSnoozed, setShowSnoozed] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const [windowDays, setWindowDays] = useState<number | null>(
    DEFAULT_WINDOW_DAYS,
  );
  // Which date the window measures. "reply" turns the list into answered
  // reviews by when the reply went up — Don Elwell (Alan Jay), 2026-09-29:
  // "what did we reply to this month?" The review-date window couldn't say:
  // a five-year-old review answered yesterday sat outside "Last 30 days".
  const [basis, setBasis] = useState<WindowBasis>("review");
  // Custom dates replace the window when set. Don Elwell (Alan Jay),
  // 2026-10-06, DP #36602: last month's reviews for the gift-card contest.
  // Picking dates is asking for a report, not the work queue, so every status
  // plus hidden and snoozed come on — and the queue filters you had are put
  // back when you leave. Without that, "September" opened on the unanswered
  // chips alone and showed 3 of 78 reviews.
  const [customRange, setCustomRange] = useState<CustomRange | null>(null);
  const preCustomFilters = useRef<{
    statuses: Set<ReviewStatus>;
    hidden: boolean;
    snoozed: boolean;
  } | null>(null);
  const customProblem = customRange ? rangeProblem(customRange) : null;
  // Flagged-first is on by default and now says so on screen. Silently pinning
  // 1–2★ reviews to position 1 is what made the queue read as unfilterable: the
  // same review sat at the top whatever you clicked.
  const [flaggedFirst, setFlaggedFirst] = useState(true);
  // "Older" flips the window instead of removing it — you get exactly the set
  // the window excluded, so the backlog is workable rather than merely visible.
  const [showOlder, setShowOlder] = useState(false);
  const [olderCount, setOlderCount] = useState<number | null>(null);
  // Reply view only: just the replies sent through Savvy, not the ones typed
  // straight into Google. Don Elwell (Alan Jay), 2026-09-30.
  const [savvyOnly, setSavvyOnly] = useState(false);
  const [viaSavvyCount, setViaSavvyCount] = useState<number | null>(null);
  // Paging. The list used to ask for 200 rows and say nothing about the rest;
  // with 1,700 in the backlog that silently dropped whole rooftops off the end.
  const [total, setTotal] = useState(0);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  // Chip counts now come from the server, each computed with its own dimension
  // dropped, so selecting one store no longer erases every other store's count.
  const [dealerCounts, setDealerCounts] = useState<Map<string, number>>(
    () => new Map(),
  );
  const [statusCounts, setStatusCounts] = useState<Map<string, number>>(
    () => new Map(),
  );

  // Only the very first load may pick a review for you. Every reload after that
  // is either a filter change (never moves the selection) or a mutation (hands
  // you the next one) — the caller says which, because a reason read when the
  // response lands can be overwritten by a second request that started first.
  const loadedOnceRef = useRef(false);

  // Overlapping requests: two quick chip clicks put two fetches in flight, and
  // whichever answers LAST used to win — Posted on→off inside ~150ms could leave
  // posted reviews on screen under an unlit Posted chip. Same guard as the
  // outreach queue: only the newest request may write.
  const reqId = useRef(0);

  // Multi-store view = no single dealer focused AND more than 1 dealer visible.
  const showStoreUi = !focusedDealerId && dealersInFocus.length > 1;
  const dealerNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of dealersInFocus) m.set(d.id, d.name);
    return m;
  }, [dealersInFocus]);

  // Reset per-store chip filter when group focus changes.
  useEffect(() => {
    setActiveStoreIds(new Set());
  }, [dealersInFocus]);

  const buildParams = useCallback(
    (offset: number) => {
      const params = new URLSearchParams();
      // The reply view is the answered set by definition — it owns status.
      if (basis === "reply") {
        params.set("basis", "reply");
        if (savvyOnly) params.set("via", "savvy");
      } else for (const s of activeStatuses) params.append("status", s);
      if (focusedDealerId) params.set("dealerId", focusedDealerId);
      // The store chips go to the server as repeatable dealerId params. They
      // used to be applied here, over the page that had already arrived — so a
      // rooftop with 234 unanswered reviews could come back showing one.
      else for (const id of activeStoreIds) params.append("dealerId", id);
      if (showSnoozed) params.set("includeSnoozed", "1");
      if (showHidden) params.set("includeHidden", "1");
      if (customRange) {
        params.set("from", customRange.from);
        params.set("to", customRange.to);
      } else if (windowDays !== null) {
        params.set("windowDays", String(windowDays));
        if (showOlder) params.set("olderOnly", "1");
      }
      if (!flaggedFirst) params.set("sort", "recent");
      params.set("limit", String(PAGE_SIZE));
      if (offset > 0) params.set("offset", String(offset));
      return params;
    },
    [
      activeStatuses,
      basis,
      savvyOnly,
      focusedDealerId,
      activeStoreIds,
      showSnoozed,
      showHidden,
      windowDays,
      customRange,
      showOlder,
      flaggedFirst,
    ],
  );

  const applyFacets = useCallback((data: ReviewsListResponse) => {
    setTotal(data.total ?? data.reviews.length);
    setNextOffset(data.nextOffset ?? null);
    setDealerCounts(new Map((data.dealerCounts ?? []).map((d) => [d.dealerId, d.n])));
    setStatusCounts(new Map((data.statusCounts ?? []).map((s) => [s.status, s.n])));
    setViaSavvyCount(data.viaSavvyCount ?? null);
  }, []);

  const fetchList = useCallback(async (reason: ReloadReason) => {
    const mine = ++reqId.current;
    // Half-typed or backwards dates: say so in the date panel and show nothing,
    // rather than leave the previous range's list under the new dates.
    if (customRange && rangeProblem(customRange)) {
      setReviews([]);
      setTotal(0);
      setNextOffset(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await apiGet<ReviewsListResponse>(
        `/api/reviews?${buildParams(0).toString()}`,
      );
      if (mine !== reqId.current) return; // superseded by a newer filter
      // No client-side sort any more: the server orders attention-first across
      // the whole queue, which a per-page sort cannot do.
      setReviews(data.reviews);
      applyFacets(data);
      // While showing the older set the server has nothing to report here, so
      // keep the last count — otherwise the chip vanishes and there's no way
      // back off it.
      if (!showOlder) setOlderCount(data.olderCount ?? null);
      // A filter change must never hand back a review you didn't ask for — see
      // lib/queue_selection.ts. Acting on a review still advances to the next.
      setSelectedId((prev) =>
        nextSelection({
          previous: prev,
          visibleIds: data.reviews.map((r) => r.id),
          reason,
        }),
      );
    } catch (err) {
      if (mine !== reqId.current) return;
      console.error("reviews load failed:", err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (mine === reqId.current) setLoading(false);
    }
  }, [buildParams, applyFacets, showOlder, customRange]);

  const loadMore = useCallback(async () => {
    if (nextOffset === null || loadingMore) return;
    const mine = reqId.current;
    setLoadingMore(true);
    try {
      const data = await apiGet<ReviewsListResponse>(
        `/api/reviews?${buildParams(nextOffset).toString()}`,
      );
      // A filter changed while this page was in flight — appending it would mix
      // two different queries into one list.
      if (mine !== reqId.current) return;
      // De-duplicate by id: a review posted between two page fetches shifts the
      // window and can otherwise deliver the same row twice.
      setReviews((prev) => {
        const seen = new Set(prev.map((r) => r.id));
        return [...prev, ...data.reviews.filter((r) => !seen.has(r.id))];
      });
      applyFacets(data);
    } catch (err) {
      console.error("reviews page load failed:", err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoadingMore(false);
    }
  }, [buildParams, applyFacets, nextOffset, loadingMore]);

  useEffect(() => {
    void fetchList(loadedOnceRef.current ? "filter" : "initial");
    loadedOnceRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeStatuses,
    basis,
    savvyOnly,
    focusedDealerId,
    activeStoreIds,
    showSnoozed,
    showHidden,
    windowDays,
    customRange,
    showOlder,
    flaggedFirst,
  ]);

  const enterCustomRange = () => {
    preCustomFilters.current = {
      statuses: activeStatuses,
      hidden: showHidden,
      snoozed: showSnoozed,
    };
    setActiveStatuses(new Set(STATUS_FILTERS.map((f) => f.value)));
    setShowHidden(true);
    setShowSnoozed(true);
    setShowOlder(false);
    setCustomRange(lastFullMonth(todayInEastern()));
  };

  const exitCustomRange = () => {
    const prev = preCustomFilters.current;
    if (prev) {
      setActiveStatuses(prev.statuses);
      setShowHidden(prev.hidden);
      setShowSnoozed(prev.snoozed);
    }
    preCustomFilters.current = null;
    setCustomRange(null);
  };

  // The CSV is the list on screen, as a file — same params, minus paging. A
  // plain link can't carry the focus headers apiGet adds, so the stores in
  // view are named explicitly; the server still intersects them with the
  // signed-in user's own scope.
  const exportHref = useMemo(() => {
    const params = buildParams(0);
    params.delete("limit");
    params.delete("offset");
    if (!params.has("dealerId")) {
      for (const d of dealersInFocus) params.append("dealerId", d.id);
    }
    return `/api/reviews/export.csv?${params.toString()}`;
  }, [buildParams, dealersInFocus]);

  const toggleStatus = (s: ReviewStatus) => {
    setActiveStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  };

  const toggleStore = (id: string) => {
    setActiveStoreIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Both counts come from the server now. Deriving them from `reviews` made
  // every chip describe the loaded page: status chips capped at the page size,
  // and every store's count vanishing as soon as one store was selected.
  const counts = statusCounts;
  const storeCounts = dealerCounts;

  // This one stays local on purpose — it counts what is actually loaded, and
  // it's the label on "excluded from Approve all", which only ever acts on
  // loaded rows.
  const attentionCount = useMemo(
    () => reviews.filter(needsAttention).length,
    [reviews],
  );

  // Approve all covers what's on screen with a drafted reply, minus anything
  // flagged — those must be opened and approved one at a time.
  const approvable = useMemo(
    () =>
      reviews.filter(
        (r) => r.status === "drafted" && r.latestDraft && !needsAttention(r),
      ),
    [reviews],
  );

  const [bulkBusy, setBulkBusy] = useState(false);
  const onApproveAll = async () => {
    if (approvable.length === 0) return;
    const flaggedNote =
      attentionCount > 0
        ? ` ${attentionCount} flagged review${attentionCount === 1 ? " is" : "s are"} excluded and must be approved individually.`
        : "";
    if (
      !window.confirm(
        `Approve ${approvable.length} drafted repl${approvable.length === 1 ? "y" : "ies"}?${flaggedNote}`,
      )
    )
      return;
    setBulkBusy(true);
    try {
      const result = await apiPost<BulkApproveResponse>(
        "/api/reviews/bulk-approve",
        { reviewIds: approvable.map((r) => r.id) },
      );
      await fetchList("mutation");
      const skippedNote =
        result.skipped.length > 0
          ? ` ${result.skipped.length} skipped (${[...new Set(result.skipped.map((s) => s.reason))].join(", ")}).`
          : "";
      alert(`Approved ${result.approved} repl${result.approved === 1 ? "y" : "ies"}.${skippedNote}`);
    } catch (err) {
      alert(
        `Approve all failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="flex h-full">
      <section
        className={[
          "w-full flex-col border-slate-200 bg-white lg:flex lg:w-[440px] lg:border-r",
          detailOpen ? "hidden" : "flex",
        ].join(" ")}
      >
        <header className="border-b border-slate-200 px-5 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-semibold text-slate-900">
              Review queue
            </h1>
            <div className="flex items-center gap-2">
              {approvable.length > 0 && (
                <button
                  onClick={() => void onApproveAll()}
                  disabled={bulkBusy}
                  className="rounded-lg bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  {bulkBusy
                    ? "Approving…"
                    : `✓ Approve all drafted (${approvable.length})`}
                </button>
              )}
              {!customProblem && (
                <a
                  href={exportHref}
                  className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  title="Download the reviews in this view as a spreadsheet"
                >
                  Download CSV
                </a>
              )}
              <button
                onClick={() => void fetchList("filter")}
                className="text-xs text-slate-500 hover:text-slate-900"
                title="Refresh"
              >
                ↻ Refresh
              </button>
            </div>
          </div>
          {attentionCount > 0 && (
            <div className="mt-3 rounded-lg border-2 border-rose-400 bg-rose-50 px-3 py-2">
              <span className="text-sm font-bold text-rose-700">
                ⚠ {attentionCount} review{attentionCount === 1 ? "" : "s"} need
                {attentionCount === 1 ? "s" : ""} attention
              </span>
              <span className="ml-2 text-xs text-rose-600">
                1–2★ or flagged — excluded from Approve all
              </span>
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-1.5">
            {basis === "reply" && (
              <span className="self-center pr-1 text-xs font-medium text-slate-600">
                {savvyOnly
                  ? "Replies sent through Savvy, newest first"
                  : "Answered reviews, newest reply first"}
              </span>
            )}
            {basis === "reply" && (
              <button
                onClick={() => setSavvyOnly((v) => !v)}
                className={`rounded-full px-2.5 py-1 text-xs font-medium transition ${
                  savvyOnly
                    ? "bg-brand-600 text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
                title={
                  savvyOnly
                    ? "Showing only replies sent through Savvy. Tap to include replies typed directly on Google."
                    : "Show only replies sent through Savvy, not ones typed directly on Google"
                }
              >
                Via Savvy only
                {viaSavvyCount !== null ? ` · ${viaSavvyCount}` : ""}
              </button>
            )}
            {basis === "review" && STATUS_FILTERS.map((f) => {
              const active = activeStatuses.has(f.value);
              return (
                <button
                  key={f.value}
                  onClick={() => toggleStatus(f.value)}
                  className={`rounded-full px-2.5 py-1 text-xs font-medium transition ${
                    active
                      ? "bg-brand-600 text-white"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                >
                  {f.label}
                  {counts.has(f.value) ? ` · ${counts.get(f.value)}` : ""}
                </button>
              );
            })}
            {/* One picker, two dates. The option value carries both, so the
                closed picker always names which date it is measuring. */}
            <select
              value={`${basis}:${customRange ? "custom" : windowDays === null ? "all" : windowDays}`}
              onChange={(e) => {
                const [b, v] = e.target.value.split(":") as [WindowBasis, string];
                if (b !== basis) {
                  // The Older count belongs to the other date — don't carry it over.
                  setBasis(b);
                  setShowOlder(false);
                  setOlderCount(null);
                  // Nor the Via Savvy filter: it's a reply-view filter, and
                  // coming back to the reply view should start from every reply.
                  setSavvyOnly(false);
                  setViaSavvyCount(null);
                }
                if (v === "custom") {
                  // Switching date basis keeps the dates you already picked.
                  if (!customRange) enterCustomRange();
                  return;
                }
                if (customRange) exitCustomRange();
                setWindowDays(v === "all" ? null : Number(v));
                // "All time" already includes the older set — the chip would be
                // a no-op, so drop out of it rather than leave it stuck on.
                if (v === "all") setShowOlder(false);
              }}
              className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 transition hover:bg-slate-200"
              title={
                basis === "reply"
                  ? "Only show reviews we replied to in this window"
                  : "Only show reviews written in this window"
              }
            >
              {(["review", "reply"] as const).map((b) => (
                <optgroup
                  key={b}
                  label={b === "reply" ? "By when we replied" : "By when the review was written"}
                >
                  {windowOptions(b === basis && showOlder, b).map((w) => (
                    <option
                      key={w.label}
                      value={`${b}:${w.days === null ? "all" : w.days}`}
                    >
                      {w.label}
                    </option>
                  ))}
                  <option value={`${b}:custom`}>
                    {b === "reply" ? "Replied between dates…" : "Custom dates…"}
                  </option>
                </optgroup>
              ))}
            </select>
            {!customRange && windowDays !== null && (showOlder || (olderCount ?? 0) > 0) && (
              <button
                onClick={() => setShowOlder((v) => !v)}
                className={`rounded-full px-2.5 py-1 text-xs font-medium transition ${
                  showOlder
                    ? "bg-slate-700 text-white"
                    : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                }`}
                title={
                  basis === "reply"
                    ? `${olderCount} ${savvyOnly ? "Savvy " : ""}repl${olderCount === 1 ? "y" : "ies"} posted more than ${windowDays} days ago, or with no known reply date`
                    : `${olderCount} unanswered review${olderCount === 1 ? "" : "s"} older than ${windowDays} days`
                }
              >
                Older · {olderCount}
              </button>
            )}
            {!customRange && windowDays !== null && showOlder && (
              <span className="self-center text-[11px] text-slate-500">
                {basis === "reply"
                  ? `showing only replies posted more than ${windowDays} days ago`
                  : `showing only reviews older than ${windowDays} days`}
              </span>
            )}
            {basis === "review" && (
            <button
              onClick={() => setFlaggedFirst((v) => !v)}
              className={`rounded-full px-2.5 py-1 text-xs font-medium transition ${
                flaggedFirst
                  ? "bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-300"
                  : "bg-slate-100 text-slate-500 hover:bg-slate-200"
              }`}
              title={
                flaggedFirst
                  ? "1–2★ and flagged reviews are pinned to the top. Turn off for newest first."
                  : "Sorted newest first. Turn on to pin 1–2★ and flagged reviews to the top."
              }
            >
              {flaggedFirst ? "⚠ Flagged first" : "Newest first"}
            </button>
            )}
            <button
              onClick={() => setShowSnoozed((v) => !v)}
              className={`rounded-full px-2.5 py-1 text-xs font-medium transition ${
                showSnoozed
                  ? "bg-amber-500 text-white"
                  : "bg-slate-100 text-slate-500 hover:bg-slate-200"
              }`}
              title="Show snoozed reviews"
            >
              Snoozed
            </button>
            <button
              onClick={() => setShowHidden((v) => !v)}
              className={`rounded-full px-2.5 py-1 text-xs font-medium transition ${
                showHidden
                  ? "bg-slate-700 text-white"
                  : "bg-slate-100 text-slate-500 hover:bg-slate-200"
              }`}
              title="Show hidden reviews"
            >
              Hidden
            </button>
          </div>
          {customRange && (
            <div className="mt-2">
              <CustomRangeFields
                range={customRange}
                onChange={setCustomRange}
                summary={
                  basis === "reply"
                    ? `Replies posted ${customRangeLabel(customRange)}, Eastern time`
                    : `Reviews written ${customRangeLabel(customRange)}, Eastern time · all statuses, hidden and snoozed included`
                }
              />
            </div>
          )}
          {showStoreUi && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {dealersInFocus.map((d) => {
                const active = activeStoreIds.has(d.id);
                const count = storeCounts.get(d.id) ?? 0;
                return (
                  <button
                    key={d.id}
                    onClick={() => toggleStore(d.id)}
                    className={`rounded-md px-2 py-0.5 text-[11px] font-medium transition ${
                      active
                        ? "bg-slate-900 text-white"
                        : count === 0
                          ? "bg-white text-slate-400 ring-1 ring-inset ring-slate-200 hover:bg-slate-50"
                          : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200 hover:bg-slate-50"
                    }`}
                    title={
                      count === 0
                        ? `${d.name} — nothing matching the current filters`
                        : `${d.name} — ${count} in the queue`
                    }
                  >
                    {/* Always show the number, including zero. A chip with no
                        count reads as "not loaded yet"; "· 0" says the filters
                        are what's empty, not the rooftop. */}
                    {d.name} · {count}
                  </button>
                );
              })}
            </div>
          )}
        </header>
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="px-5 py-6 text-sm text-slate-500">Loading…</div>
          )}
          {error && (
            <div className="px-5 py-6 text-sm text-rose-700">{error}</div>
          )}
          {!loading && reviews.length === 0 && !error && (
            <div className="px-5 py-8 text-center text-sm text-slate-500">
              {customProblem
                ? "Fix the dates above to see reviews."
                : basis === "reply"
                  ? savvyOnly
                    ? "No replies sent through Savvy in this window."
                    : "No replies posted in this window."
                  : customRange
                    ? "No reviews in these dates."
                    : "Nothing here needs you right now."}
            </div>
          )}
          <ul>
            {reviews.map((r) => {
              const attention = needsAttention(r);
              return (
              <li key={r.id}>
                <button
                  onClick={() => {
                    setSelectedId(r.id);
                    setDetailOpen(true);
                  }}
                  className={`flex w-full flex-col gap-1 border-b border-slate-100 px-5 py-3 text-left transition ${
                    attention ? "border-l-4 border-l-rose-500 " : ""
                  }${
                    selectedId === r.id
                      ? "bg-brand-50"
                      : attention
                        ? "bg-rose-50 hover:bg-rose-100"
                        : "hover:bg-slate-50"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <StarRating stars={r.stars} size="sm" />
                    <span className="text-xs text-slate-500">
                      {basis === "reply"
                        ? r.repliedAt
                          ? `Replied ${timeAgo(r.repliedAt)}`
                          : "Reply date unknown"
                        : timeAgo(r.postedAt)}
                    </span>
                  </div>
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {r.reviewerName ?? "Anonymous"}
                  </div>
                  <div className="line-clamp-2 text-xs text-slate-600">
                    {r.text ?? (
                      <span className="italic text-slate-400">
                        (no comment)
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    {attention && (
                      <span className="rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                        ⚠ Needs attention
                      </span>
                    )}
                    {r.hiddenAt && (
                      <span className="rounded-full bg-slate-700 px-2 py-0.5 text-[10px] font-medium text-white">
                        Hidden
                      </span>
                    )}
                    {!r.hiddenAt && isSnoozed(r) && (
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800 ring-1 ring-inset ring-amber-300">
                        Snoozed until{" "}
                        {new Date(r.snoozedUntil!).toLocaleDateString()}
                      </span>
                    )}
                    <StatusBadge status={r.status} />
                    {replySource(r) && (
                      <span className="text-[10px] text-slate-500">
                        {replySource(r)}
                        {basis === "reply" && ` · review ${timeAgo(r.postedAt)}`}
                      </span>
                    )}
                    {showStoreUi && dealerNameById.get(r.dealerId) && (
                      <span
                        className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 ring-1 ring-inset ring-slate-200"
                        title={dealerNameById.get(r.dealerId)}
                      >
                        {dealerNameById.get(r.dealerId)}
                      </span>
                    )}
                  </div>
                </button>
              </li>
              );
            })}
          </ul>
          {/* What the page is NOT showing. The old list stopped at 200 rows and
              said nothing, so a 1,700-review backlog looked finished. */}
          {!loading && !error && total > 0 && (
            <div className="border-t border-slate-100 px-5 py-4 text-center">
              <div className="text-xs text-slate-500">
                Showing {reviews.length} of {total}
              </div>
              {nextOffset !== null && (
                <button
                  onClick={() => void loadMore()}
                  disabled={loadingMore}
                  className="mt-2 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-200 disabled:opacity-50"
                >
                  {loadingMore
                    ? "Loading…"
                    : `Load ${Math.min(PAGE_SIZE, total - reviews.length)} more`}
                </button>
              )}
            </div>
          )}
        </div>
      </section>
      <section
        className={[
          "min-w-0 flex-1 flex-col overflow-y-auto bg-slate-50 lg:flex",
          detailOpen ? "flex" : "hidden",
        ].join(" ")}
      >
        <button
          type="button"
          onClick={() => setDetailOpen(false)}
          className="sticky top-0 z-10 flex items-center gap-2 border-b border-slate-200 bg-white px-4 py-3 text-left text-sm font-medium text-slate-700 lg:hidden"
        >
          <span aria-hidden>←</span> Back to queue
        </button>
        {selectedId ? (
          <ReviewDetail
            reviewId={selectedId}
            onMutated={() => void fetchList("mutation")}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-slate-500">
            Pick a review on the left to read it and reply.
          </div>
        )}
      </section>
    </div>
  );
}
